import Image from 'next/image'
import React, { FC } from 'react'

import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import toast from 'react-hot-toast'
import { useSelector } from 'react-redux'
import {
  Field,
  FormErrors,
  getFormAsyncErrors,
  InjectedFormProps,
  reduxForm,
  WrappedFieldProps
} from 'redux-form'

import { Box, Container, TextField } from '@material-ui/core'

import ExtendedButton from '~/components/shared/ExtendedButton'
import FormControl from '~/components/shared/FormControl'
import Text, { Title } from '~/components/shared/Text'

import SubscribeSchema from '~/schemas/subscibe'
import EmpriseGroup from '~/services/EmpriseGroup'
import { AppState } from '~/state/store'
import { asyncValidate } from '~/utils/asyncValidate'
import { createSubmit } from '~/utils/createSubmit'
import ErrorHandler from '~/utils/errorHandler'

import TelegramIcon from '~/assets/icons/telegram.svg?sprite'

import { getEntityByBrand } from '~/brands'
import { SubscribeNewsletterInput } from '~/interfaces/inputs'

import bgImageBoat from '~/../public/static/images/subscribe.boat.jpg'
import bgImageRv from '~/../public/static/images/subscribe.rv.jpg'

const bgImage = getEntityByBrand<string>({ rv: bgImageRv, boat: bgImageBoat })

const TextFieldComponent = ({
  input,
  meta: _meta,
  ...rest
}: WrappedFieldProps) => (
  <TextField
    fullWidth
    variant="standard"
    InputProps={{
      disableUnderline: true
    }}
    {...input}
    {...rest}
  />
)

const SubscribeSection: FC<InjectedFormProps<SubscribeNewsletterInput>> = ({
  form,
  handleSubmit
}) => {
  const { executeRecaptcha } = useGoogleReCaptcha()

  const errors = useSelector<AppState, FormErrors<{ email: string }>>(
    getFormAsyncErrors(form)
  )

  const onSubmit = handleSubmit(async (values) => {
    try {
      if (!executeRecaptcha) throw new Error('ReCaptcha is not loaded yet')

      const captchaToken = await executeRecaptcha('subscribe_email_newsletter')

      await EmpriseGroup.emailSubscribe({
        email: values.email,
        captcha_token: captchaToken
      })
    } catch (e) {
      throw new ErrorHandler(e)
    }
  })

  return (
    <Box position="relative" display="flex" color="common.white" height={350}>
      <Image
        layout="fill"
        objectFit="cover"
        objectPosition="center"
        alt="subscribe"
        placeholder="blur"
        src={bgImage}
      />
      <Container
        maxWidth="xs"
        component="form"
        style={{ position: 'relative' }}
        onSubmit={createSubmit({ handleSubmit: onSubmit })}
      >
        <Title
          mt={6}
          mb={2}
          color="inherit"
          variant="h1"
          as="h2"
          textAlign="center"
          lineHeight={1.5}
        >
          Subscribe
        </Title>
        <Text textAlign="center" color="inherit" mb={4}>
          Subscribe to our email newsletter
        </Text>
        <FormControl
          errorMessage={errors?.email as string}
          style={{ margin: '0 auto' }}
        >
          <Box bgcolor="common.white" borderRadius={4} display="flex">
            <Box padding={2} width="100%">
              <Field
                id="email"
                placeholder="Email Address"
                name="email"
                autoComplete="email"
                component={TextFieldComponent}
              />
            </Box>
            <ExtendedButton type="submit">
              <TelegramIcon />
            </ExtendedButton>
          </Box>
        </FormControl>
      </Container>
    </Box>
  )
}

const withForm = reduxForm<SubscribeNewsletterInput>({
  form: 'Subscribe_Form',
  shouldAsyncValidate: () => true,
  asyncValidate: asyncValidate(SubscribeSchema),
  destroyOnUnmount: true,
  asyncBlurFields: [],
  onSubmitSuccess: (_values, _dispatch, { reset }) => {
    toast.success('Success!')
    reset()
  }
})

export default withForm(SubscribeSection)
